const requiredConfigs = [
    'REACT_APP_HOST',
    'REACT_APP_PROTOCOL',
    'REACT_APP_BACKEND_URL',
    'REACT_APP_PATTERNLIB_URL',
];

type Return = {
    baseUrl: string;
    backendUrl: string;
    backendApi: string;
    backendProxyTo?: string;
    /** Pattern Library 2.0 catalogue origin, no trailing slash */
    patternLibraryUrl: string;
};

const getConfig = (): Return => {
    // Vite inlines process.env at build time; the old dotenv fallback was a
    // Node-only path that a bundler would try to resolve.
    const vars = process.env;
    if (!vars) {
        throw new Error('No config vars found');
    }
    const configKeys = Object.keys(vars);

    const missingFields = requiredConfigs.filter(
        (field) => !configKeys.includes(field),
    );
    if (missingFields.length) {
        throw new Error(
            'The following required configuration fields are missing: ' +
                missingFields.toString(),
        );
    }
    const {
        REACT_APP_HOST: HOST,
        REACT_APP_PORT: PORT,
        REACT_APP_PROTOCOL: PROTOCOL,
        REACT_APP_BACKEND_URL: BACKEND_URL,
        REACT_APP_PROXY_BACKEND_TO: backendProxyTo,
        REACT_APP_ENVIRONMENT: envType,
        REACT_APP_PATTERNLIB_URL: PATTERNLIB_URL,
        REACT_APP_PROXY_PATTERNLIB_URL: patternLibraryProxyTo,
        REACT_APP_USE_LOCAL_BACKEND: useLocalBackend,
    } = vars;

    if (
        typeof HOST !== 'string' ||
        typeof PROTOCOL !== 'string' ||
        typeof BACKEND_URL !== 'string'
    ) {
        throw new Error(
            'The following required configuration fields are missing: ' +
                missingFields.toString(),
        );
    }

    const getPort = (): string | null => {
        if (!envType || !['local', 'cypress'].includes(envType)) {
            return null;
        }

        return PORT ?? null;
    };

    const port = getPort();

    const getBaseUrl = (): string => {
        const allowedProtocols = ['http', 'https'];
        if (!allowedProtocols.includes(PROTOCOL)) {
            throw new Error(
                'PROTOCOL can only be one of: ' + allowedProtocols.toString(),
            );
        }

        return PROTOCOL + '://' + HOST + (port ? `:${port}` : '') + '/';
    };

    const baseUrl = getBaseUrl();

    const getBackendUrl = (): string => {
        if (useLocalBackend === 'true') {
            return BACKEND_URL;
        }
        if (
            !!backendProxyTo &&
            backendProxyTo !== '' &&
            backendProxyTo !== 'none'
        ) {
            return window.location.origin + '/';
        }
        return BACKEND_URL;
    };
    const backendUrl = getBackendUrl();

    // The catalogue API sends no CORS headers yet; dev goes through Vite's
    // /patternlib proxy rule when REACT_APP_PROXY_PATTERNLIB_URL is set.
    const getPatternLibraryUrl = (): string => {
        if (!!patternLibraryProxyTo) {
            return baseUrl + 'patternlib';
        }
        return (PATTERNLIB_URL ?? '').replace(/\/$/, '');
    };
    const patternLibraryUrl = getPatternLibraryUrl();

    return {
        baseUrl,
        backendUrl,
        backendApi: backendUrl + 'api/',
        backendProxyTo,
        patternLibraryUrl,
    };
};

export const { baseUrl, backendUrl, backendApi, patternLibraryUrl } =
    getConfig();
export default getConfig;
